package com.fantasytenis.web.components

import androidx.compose.runtime.*
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.ThemeColors
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.math.abs

/**
 * SweatMeter - Payout proximity visualization widget
 *
 * Shows how close the user is to the payout threshold with:
 * - Visual progress bar
 * - Pulsing animation when close
 * - Color-coded urgency (green=safe, yellow=close, red=danger)
 * - Points gap to payout/next rank
 */
@Composable
fun SweatMeter(
    currentRank: Int,
    currentPoints: Double,
    payoutThreshold: Int,
    pointsToPayout: Double,
    pointsToNextRank: Double,
    totalEntries: Int,
    colors: ThemeColors
) {
    val inPayout = currentRank in 1..payoutThreshold
    val isClose = !inPayout && pointsToPayout <= 5.0 && pointsToPayout > 0
    val isDanger = !inPayout && pointsToPayout <= 2.0 && pointsToPayout > 0

    // Determine status
    val status = when {
        inPayout && currentRank <= 3 -> SweatStatus.CRUSHING_IT
        inPayout -> SweatStatus.IN_THE_MONEY
        isDanger -> SweatStatus.SWEATING
        isClose -> SweatStatus.IN_THE_HUNT
        else -> SweatStatus.CLIMBING
    }

    Div({
        classes("sweat-meter")
        if (status == SweatStatus.SWEATING || status == SweatStatus.IN_THE_HUNT) {
            classes("sweat-meter-pulsing")
        }
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(16.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
            property("border", "2px solid ${status.borderColor}")
            if (status == SweatStatus.SWEATING) {
                property("animation", "sweatPulse 1s ease-in-out infinite")
            }
        }
    }) {
        // Header row
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(12.px)
            }
        }) {
            // Status badge
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Span({ style { fontSize(20.px) } }) {
                    Text(status.emoji)
                }
                Span({
                    style {
                        fontSize(14.px)
                        fontWeight(700)
                        color(Color(status.textColor))
                        property("text-transform", "uppercase")
                        letterSpacing(0.5.px)
                    }
                }) {
                    Text(status.label)
                }
            }

            // Rank badge
            Div({
                style {
                    backgroundColor(
                        when (currentRank) {
                            1 -> Colors.Medal.GoldBackground
                            2 -> Colors.Medal.SilverBackground
                            3 -> Colors.Medal.BronzeBackground
                            else -> colors.surfaceVariant
                        }
                    )
                    color(
                        when (currentRank) {
                            1 -> Colors.Medal.GoldText
                            2 -> Colors.Medal.SilverText
                            3 -> Colors.Medal.BronzeText
                            else -> colors.textPrimary
                        }
                    )
                    padding(6.px, 12.px)
                    borderRadius(20.px)
                    fontWeight(700)
                    fontSize(14.px)
                }
            }) {
                Text("#$currentRank")
            }
        }

        // Points display
        Div({
            style {
                textAlign("center")
                marginBottom(16.px)
            }
        }) {
            Div({
                style {
                    fontSize(36.px)
                    fontWeight(700)
                    color(colors.primary)
                }
            }) {
                Text("${currentPoints.asDynamic().toFixed(1)}")
            }
            Div({
                style {
                    fontSize(12.px)
                    color(colors.textSecondary)
                    property("text-transform", "uppercase")
                    letterSpacing(1.px)
                }
            }) {
                Text("TOTAL POINTS")
            }
        }

        // Progress bar to payout
        if (payoutThreshold > 0) {
            Div({
                style {
                    marginBottom(12.px)
                }
            }) {
                // Progress bar background
                Div({
                    style {
                        width(100.percent)
                        height(8.px)
                        backgroundColor(colors.surfaceVariant)
                        borderRadius(4.px)
                        overflow("hidden")
                        position(Position.Relative)
                    }
                }) {
                    // Progress fill
                    val progressPercent = if (totalEntries > 0) {
                        ((totalEntries - currentRank + 1).toDouble() / totalEntries * 100).coerceIn(0.0, 100.0)
                    } else 0.0

                    // Payout threshold marker
                    val payoutMarkerPercent = if (totalEntries > 0) {
                        ((totalEntries - payoutThreshold).toDouble() / totalEntries * 100).coerceIn(0.0, 100.0)
                    } else 0.0

                    Div({
                        style {
                            width(progressPercent.percent)
                            height(100.percent)
                            backgroundColor(Color(status.progressColor))
                            property("transition", "width 0.3s ease")
                        }
                    })

                    // Payout line marker
                    Div({
                        style {
                            position(Position.Absolute)
                            left(payoutMarkerPercent.percent)
                            top((-2).px)
                            width(2.px)
                            height(12.px)
                            backgroundColor(Colors.Success)
                        }
                    })
                }

                // Labels
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        marginTop(4.px)
                        fontSize(11.px)
                        color(colors.textSecondary)
                    }
                }) {
                    Span { Text("#$totalEntries") }
                    Span({
                        style {
                            color(Colors.Success)
                            fontWeight(600)
                        }
                    }) {
                        Text("Top $payoutThreshold pays")
                    }
                    Span { Text("#1") }
                }
            }
        }

        // Gap info
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceAround)
                paddingTop(12.px)
                property("border-top", "1px solid ${colors.border}")
            }
        }) {
            // Points to payout
            if (!inPayout && pointsToPayout > 0) {
                GapStat(
                    label = "To payout",
                    value = "${pointsToPayout.asDynamic().toFixed(1)} pts",
                    isUrgent = isDanger,
                    colors = colors
                )
            } else if (inPayout) {
                GapStat(
                    label = "Payout buffer",
                    value = "+${abs(pointsToPayout).asDynamic().toFixed(1)} pts",
                    isPositive = true,
                    colors = colors
                )
            }

            // Points to next rank
            if (currentRank > 1 && pointsToNextRank > 0) {
                GapStat(
                    label = "To #${currentRank - 1}",
                    value = "${pointsToNextRank.asDynamic().toFixed(1)} pts",
                    colors = colors
                )
            }
        }
    }
}

@Composable
private fun GapStat(
    label: String,
    value: String,
    isUrgent: Boolean = false,
    isPositive: Boolean = false,
    colors: ThemeColors
) {
    Div({
        style {
            textAlign("center")
        }
    }) {
        Div({
            style {
                fontSize(11.px)
                color(colors.textSecondary)
                marginBottom(2.px)
            }
        }) {
            Text(label)
        }
        Div({
            style {
                fontSize(16.px)
                fontWeight(700)
                color(
                    when {
                        isUrgent -> Colors.Error
                        isPositive -> Colors.Success
                        else -> colors.textPrimary
                    }
                )
            }
        }) {
            Text(value)
        }
    }
}

/**
 * Compact sweat meter for inline display
 */
@Composable
fun SweatMeterCompact(
    inPayout: Boolean,
    pointsToPayout: Double,
    payoutThreshold: Int,
    colors: ThemeColors
) {
    val isClose = !inPayout && pointsToPayout <= 5.0 && pointsToPayout > 0
    val isDanger = !inPayout && pointsToPayout <= 2.0 && pointsToPayout > 0

    Div({
        if (isDanger) classes("sweat-meter-pulsing")
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            padding(8.px, 12.px)
            borderRadius(8.px)
            backgroundColor(
                when {
                    inPayout -> Color("rgba(76, 175, 80, 0.15)")
                    isDanger -> Color("rgba(244, 67, 54, 0.15)")
                    isClose -> Color("rgba(255, 152, 0, 0.15)")
                    else -> colors.surfaceVariant
                }
            )
            if (isDanger) {
                property("animation", "sweatPulse 1s ease-in-out infinite")
            }
        }
    }) {
        // Icon
        Span({ style { fontSize(16.px) } }) {
            Text(
                when {
                    inPayout -> "\uD83C\uDFC6" // Trophy
                    isDanger -> "\uD83D\uDD25" // Fire
                    isClose -> "\u26A1" // Lightning
                    else -> "\uD83D\uDCC8" // Chart
                }
            )
        }

        // Text
        Span({
            style {
                fontSize(13.px)
                fontWeight(600)
                color(
                    when {
                        inPayout -> Colors.Success
                        isDanger -> Colors.Error
                        isClose -> Colors.Warning
                        else -> colors.textSecondary
                    }
                )
            }
        }) {
            Text(
                when {
                    inPayout -> "In the money!"
                    isDanger -> "${pointsToPayout.asDynamic().toFixed(1)} pts to payout!"
                    isClose -> "${pointsToPayout.asDynamic().toFixed(1)} pts from payout"
                    else -> "Climb to top $payoutThreshold"
                }
            )
        }
    }
}

private enum class SweatStatus(
    val label: String,
    val emoji: String,
    val borderColor: String,
    val textColor: String,
    val progressColor: String
) {
    CRUSHING_IT(
        label = "Crushing It!",
        emoji = "\uD83D\uDC51", // Crown
        borderColor = "#FFD700",
        textColor = "#B8860B",
        progressColor = "#FFD700"
    ),
    IN_THE_MONEY(
        label = "In The Money",
        emoji = "\uD83C\uDFC6", // Trophy
        borderColor = "#4CAF50",
        textColor = "#2E7D32",
        progressColor = "#4CAF50"
    ),
    IN_THE_HUNT(
        label = "In The Hunt",
        emoji = "\u26A1", // Lightning
        borderColor = "#FF9800",
        textColor = "#E65100",
        progressColor = "#FF9800"
    ),
    SWEATING(
        label = "Sweating!",
        emoji = "\uD83D\uDD25", // Fire
        borderColor = "#F44336",
        textColor = "#C62828",
        progressColor = "#F44336"
    ),
    CLIMBING(
        label = "Climbing",
        emoji = "\uD83D\uDCC8", // Chart
        borderColor = "transparent",
        textColor = "#666666",
        progressColor = "#2196F3"
    )
}
